import React, { useEffect } from 'react'
import classNames from 'classnames'
import s from './GeoentityListItem.less'
import CornerShops from './CornerShops'
import Reviews from './Reviews'
import GoToGeoentityButton from './GoToGeoentityButton'
import { useSelector } from 'react-redux'
import CrossBrowserImage from '../genericComponents/CrossBrowserImage'
import {
  selectCurrentHistoryRoute,
  isAddressRoute,
  selectCurrentHistoryRouteOptions
} from '../../store/history/history.selectors'
import RectangleActionButton, {
  COLOR_ORANGE_EMPTY,
  COLOR_GREY_EMPTY
} from '../genericComponents/buttons/RectangleActionButton'
import useCommonGeoentityClickTagProps from './useCommonGeoentityClickTagProps'
import { PUB_TAG } from '../analytics/PublisherTagsService'
import {
  selectGeoentityImagesAlt,
  selectReservationLink,
  selectCurrentGeoentityId,
  selectShouldDisplaySubHeaderForFilters
} from '../../store/geoentity/geoentity.selectors'
import { useAction } from '../utils/react-redux'
import { focusGeoentity, unfocusGeoentity } from '../../store/geoentity/geoentity.actions'
import { RESERVATION_URL_TYPE_LIST } from '../utils/geoentity'
import { isGE } from '../Device'
import useEventCallback from '../utils/useEventCallback'
import PromoIcon from './promo.svg'
import SVGIcon from '../svg/SVGIcon'
import { selectGeoentitySeoDomOptions } from '../../store/seo/seo.selectors'
import { useScrollContext } from '../genericComponents/ResponsiveScrollableWrapper/ResponsiveScrollableWrapper'
import useComputedHeaderHeight from '../ui/useHeaderHeight'
import { PREHEADER_HEIGHT } from '../header/header-constants'
import useTagBiForGeoentity from './useTagBiForGeoentity'
import { SPD_GEOENTITY_LIST_LINK } from '../analytics/BiTagger'

const useContextPoiDisplayOptions = () => {
  const route = useSelector(selectCurrentHistoryRoute)
  const defaultDisplay = {
    address: true,
    rubric: false
  }
  if (isAddressRoute(route))
    return {
      ...defaultDisplay,
      address: false,
      rubric: true
    }
  return defaultDisplay
}

const ReservationButton = ({ reservationUrl, clickTagPropsForRedirection }) => {
  const sendBiTagNow = useTagBiForGeoentity(SPD_GEOENTITY_LIST_LINK, { targeturl: reservationUrl })
  return (
    <span className={classNames(s.button, s.buttonForeground)}>
      <RectangleActionButton
        label="Réserver"
        color={COLOR_ORANGE_EMPTY}
        tagData={clickTagPropsForRedirection}
        href={reservationUrl}
        targetBlank={true}
        nofollow={true}
        onClick={() => {
          sendBiTagNow()
        }}
      />
    </span>
  )
}

const getLiItemProps = ({ hasItemScope }) =>
  hasItemScope
    ? {
        itemScope: true,
        itemProp: 'itemListElement',
        itemType: 'https://schema.org/ListItem'
      }
    : {}

const useLiMouseListeners = ({ id }) => {
  const execFocusGeoentity = useAction(focusGeoentity)
  const execUnfocusGeoentity = useAction(unfocusGeoentity)

  const onMouseEnter = useEventCallback(() => execFocusGeoentity(id), [execFocusGeoentity, id])
  const onMouseLeave = useEventCallback(() => execUnfocusGeoentity(id), [execUnfocusGeoentity, id])

  return isGE()
    ? {
        onMouseEnter,
        onMouseLeave
      }
    : {}
}

const getCookingType = (prestations = '') => /Types de cuisine : ([^,]+)/.exec(prestations)?.[1]

const GeoentityListItem = ({ poi, noExternalButtons, index }) => {
  const { id, name, rubric = {}, openingHours, cornerShops, additionalInfo = {}, addressLabel, reviews } = poi
  const { teasing, secondTeasing, promo, prestations } = additionalInfo
  const illustrationUrl = additionalInfo?.illustration?.url
  const scrollContext = useScrollContext()
  const reservationUrl = useSelector(state =>
    selectReservationLink(state, { geoentity: poi, type: RESERVATION_URL_TYPE_LIST })
  )
  const cookingType = getCookingType(prestations)
  const { label: rubricLabel } = rubric
  const displayOptions = useContextPoiDisplayOptions()
  const clickTagPropsForRedirection = useCommonGeoentityClickTagProps(
    PUB_TAG.PUB_LISTEREPONSE_REDIRECTIONRESERVATION,
    poi
  )
  const geoentityImageAlt = useSelector(store => selectGeoentityImagesAlt(store, { geoentity: poi }))
  const selectedGeoentityId = useSelector(selectCurrentGeoentityId)
  const isSelected = poi.id === selectedGeoentityId

  const { hasItemScope } = useSelector(selectGeoentitySeoDomOptions)
  const liProps = getLiItemProps({ hasItemScope })
  const liMouseListeners = useLiMouseListeners({ id })

  const { scrollTop } = useSelector(selectCurrentHistoryRouteOptions)

  const shouldDisplaySubHeaderForFilters = useSelector(selectShouldDisplaySubHeaderForFilters)

  const computedHeaderHeight = useComputedHeaderHeight(false, shouldDisplaySubHeaderForFilters) + PREHEADER_HEIGHT

  useEffect(() => {
    if (isGE() && isSelected) {
      const selectedElement = document.querySelector(`li.${s.selectedItem}`)

      if (scrollTop && selectedElement?.getBoundingClientRect) {
        scrollContext.scrollTo(
          selectedElement.getBoundingClientRect().top + scrollContext.getScrollValue() - computedHeaderHeight
        )
      }
    }
  }, [isSelected, computedHeaderHeight])

  return (
    <li className={classNames(s.item, isSelected && s.selectedItem)} {...liProps} {...liMouseListeners}>
      {hasItemScope && (
        <span itemProp="position" style={{ display: 'none' }}>
          {index}
        </span>
      )}
      <div className={s.content}>
        <div className={s.name}>
          <GoToGeoentityButton poi={poi} className={s.link} isLink hasItemScope={hasItemScope}>
            {name}
          </GoToGeoentityButton>
          {promo && (
            <div className={s.promo}>
              <img src={PromoIcon} alt="Promo icône" /> Promo
            </div>
          )}
        </div>
        {displayOptions.address && <div className={s.text}>{addressLabel}</div>}
        {displayOptions.rubric && <div className={s.text}>{rubricLabel}</div>}
        {cookingType && (
          <div className={s.cookingType}>
            <SVGIcon icon="resto-bar" />
            <div className={s.text}>{cookingType}</div>
          </div>
        )}
        <div className={s.text}>
          <Reviews reviews={reviews} />
        </div>
        {teasing && <div className={s.text} dangerouslySetInnerHTML={{ __html: teasing }} />}
        {secondTeasing && <div className={s.text} dangerouslySetInnerHTML={{ __html: secondTeasing }} />}
        {openingHours && <div className={s.text}>{openingHours}</div>}
        <CornerShops cornerShops={cornerShops} />
        <div className={s.button}>
          <GoToGeoentityButton poi={poi} hasItemScope={hasItemScope}>
            <RectangleActionButton
              label="Plus d'infos"
              color={COLOR_GREY_EMPTY}
              className={noExternalButtons ? s.fakeButtonLandingPage : s.buttonMoreInfo}
            />
          </GoToGeoentityButton>
          {!noExternalButtons && reservationUrl && (
            <ReservationButton
              reservationUrl={reservationUrl}
              clickTagPropsForRedirection={clickTagPropsForRedirection}
            />
          )}
        </div>
      </div>
      <CrossBrowserImage className={s.illustration} src={illustrationUrl} alt={geoentityImageAlt} />
      <SVGIcon className={classNames(s.chevron, s.chevronCenter)} icon="chevron-invers" />
      <SVGIcon className={classNames(s.chevron, s.chevronLeft)} icon="chevron-invers" />
      <SVGIcon className={classNames(s.chevron, s.chevronRight)} icon="chevron-invers" />
    </li>
  )
}

export default GeoentityListItem
